import { FormattedMessage, useIntl } from 'react-intl'
import { Form, Field, FieldRenderProps, useForm } from 'react-final-form'
import {
  Button,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Paper,
  TextField,
  Box,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material'
import validateSendEmail from '../../validators/validateSendEmail'
import { Link } from 'react-router-dom'
import TextFieldReduxForm from '../Common/TextFieldReduxForm'
import { IContactUsForm, IRecaptchaToken } from '../../interface'
import { useEffect } from 'react'
import { US, allCountries } from 'country-region-data'
import Recaptcha from '../Common/Recaptcha'

/**
 * Contact Us form
 * @param onSubmit On submit function
 * @param initialValuesData The initial values of the form
 * @param isLoading Whether the form is loading
 * @param disabledField Boolean value indicating whether form Fields are disabled
 * @returns Form render
 */
const ContactUsForm = (props: {
  onSubmit: (data: IContactUsForm) => void
  initialValuesData: IContactUsForm | {}
  isLoading: boolean
  disabledField: boolean
  countryCode: string | undefined
  stateSelected?: string
  setCountryCode: React.Dispatch<React.SetStateAction<string | undefined>>
  setStateCode?: React.Dispatch<React.SetStateAction<string>>
  recaptchaToken: IRecaptchaToken
  handleTokenChange: (token: IRecaptchaToken) => void
}) => {
  const intl = useIntl()
  const {
    onSubmit,
    initialValuesData,
    isLoading,
    disabledField,
    countryCode,
    setCountryCode,
    setStateCode,
    stateSelected,
    recaptchaToken,
    handleTokenChange
  } = props
  const usStatesList = US[2]
  const disabledByRecaptcha = !recaptchaToken && process.env.REACT_APP_is_test !== 'true'
  useEffect(() => {
    setCountryCode(countryCode)
    if (countryCode === 'US' && setStateCode) setStateCode('none')
  }, [countryCode])

  useEffect(() => {
    setStateCode && stateSelected && setStateCode(stateSelected)
  }, [stateSelected])

  /**
   * Checkbox component to be passed as prop on Field
   * @param params Field params from the form
   * @return html tag containing the checkbox and the renderFromHelper function
   */
  const renderCheckbox = (params: FieldRenderProps<boolean>) => {
    const { meta, input, title, dataCy } = params

    return (
      <Box sx={{ textAlign: 'left' }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={input.value ? true : false}
              onChange={(event) => input.onChange(event.target.checked)}
              color='primary'
            />
          }
          label={title}
          data-cy={dataCy}
        />
        <RenderFromHelper touched={meta.touched!} error={meta.error} isCheckbox />
      </Box>
    )
  }

  /**
   * Render error message for the checkbox field
   * @param touched A boolean value to control error
   * @param error The actual error
   * @returns a HelperText component with the error message
   */
  const RenderFromHelper = ({ touched, error, isCheckbox }: { touched: boolean; error: any; isCheckbox?: boolean }) => {
    if (!(touched && error)) {
      return null
    } else {
      return (
        <FormHelperText sx={{ color: '#f44336', ml: isCheckbox ? 4 : 2, mt: -1.5 }}>{touched && error}</FormHelperText>
      )
    }
  }

  /**
   * Text area component to be passed as prop by Field
   * @param params Values from Field form
   * @returns HTML textarea element
   */
  const renderTextArea = ({ input, title }: FieldRenderProps<string>) => {
    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const trimmedValue = event.target.value.trim()
      if (trimmedValue !== input.value) {
        input.onChange(trimmedValue)
      }
      input.onBlur(event)
    }
    return <TextField {...input} multiline placeholder={title} rows={5} onBlur={handleBlur} />
  }

  const RenderSelect = ({ fieldName, children }: { fieldName: string; children: React.ReactNode }) => {
    const value = fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
    const form = useForm()

    return (
      <Field name={fieldName}>
        {({ input, meta }) => (
          <>
            <Select
              disabled={fieldName === 'country' && disabledField}
              sx={{ textAlign: 'left' }}
              value={input.value}
              fullWidth
              onChange={(event) => {
                const value = event.target.value
                if (fieldName === 'country') setCountryCode(value)
                if (fieldName === 'state' && setStateCode) setStateCode(value)
                if (value !== 'US') {
                  form.change('state', 'none')
                }
                input.onChange(value)
              }}
              data-cy={`${fieldName}-dropdown`}
              error={meta.touched && meta.invalid}
            >
              <MenuItem value='none' disabled>
                <FormattedMessage id={`sales.select${value}`} />
              </MenuItem>
              {children}
            </Select>
            <RenderFromHelper touched={meta.touched!} error={meta.error} />
          </>
        )}
      </Field>
    )
  }

  return (
    <Paper
      data-gtm-click-subsection1='Contact Us Form'
      sx={{ maxWidth: 800, p: 4, px: { xs: 2, sm: 4 }, m: 'auto', width: '100%' }}
    >
      <Form validate={validateSendEmail} onSubmit={onSubmit} initialValues={initialValuesData}>
        {({ handleSubmit, pristine, submitting }) => (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Field
              disabled={disabledField}
              name='name'
              title={intl.formatMessage({ id: 'contactUs.name' })}
              component={TextFieldReduxForm}
              dataCy='contactus-form-name'
            />
            <Field
              disabled={disabledField}
              name='email'
              title={intl.formatMessage({ id: 'account.email' })}
              component={TextFieldReduxForm}
              dataCy='contactus-form-email'
            />
            <Field
              type='tel'
              name='phone'
              title={intl.formatMessage({ id: 'account.phone' })}
              component={TextFieldReduxForm}
              dataCy='contactus-form-phone'
            />
            <RenderSelect fieldName='country'>
              {allCountries.map(([country, countryCode], index) => (
                <MenuItem value={countryCode} key={index} data-cy={`country-dropdownItem-${countryCode}`}>
                  {country}
                </MenuItem>
              ))}
            </RenderSelect>
            {countryCode === 'US' && (
              <RenderSelect fieldName='state'>
                {usStatesList.map(([state, stateCode], index) => (
                  <MenuItem value={stateCode} key={index} data-cy={`state-dropdownItem-${stateCode}`}>
                    {state}
                  </MenuItem>
                ))}
              </RenderSelect>
            )}
            <Field
              name='organization'
              title={intl.formatMessage({ id: 'account.company' })}
              component={TextFieldReduxForm}
              dataCy='contactus-form-organization'
            />
            <Field name='comment' title={intl.formatMessage({ id: 'contactUs.comment' })} component={renderTextArea} />
            <Field
              name='acceptTerms'
              title={
                <FormattedMessage
                  id='account.iHaveReadAndAccepted'
                  values={{
                    privacyPolicy: (
                      <Link to='/legal/privacy' target='_blank' rel='noopener noreferrer'>
                        <FormattedMessage id='account.privacyPolicy' />
                      </Link>
                    )
                  }}
                />
              }
              component={renderCheckbox}
              dataCy='contactus-privacy-checkbox'
            />
            <Recaptcha onChange={handleTokenChange} />
            <Button
              id='submit-button'
              data-gtm-click-subsection2='primary-cta'
              type='submit'
              disabled={pristine || submitting || isLoading || disabledByRecaptcha}
              variant='contained'
              color='primary'
              size='large'
              data-cy='contactus-send-email-button'
            >
              {isLoading ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                <FormattedMessage id='contactUs.sendEmail' />
              )}
            </Button>
          </form>
        )}
      </Form>
    </Paper>
  )
}

export default ContactUsForm
